import { useMemo, useState } from "react";
import type { IntervieweeCard, IntervieweeDetail } from "../../../domain/contracts";
import type { IntervieweeViolationItem } from "../../../services/intervieweeViolations";
import { extractIntervieweeViolationTags, filterIntervieweeViolationTags, getIntervieweeViolations } from "../../../services/intervieweeViolations";

type TimelineItem = {
  primary: string | number;
  secondary: string;
};

type IntervieweeProfileMeta = {
  summaryTags: string[];
  violationItems: IntervieweeViolationItem[];
  activeViolationText: string;
  activeViolation: string | null;
  setActiveViolation: (next: string | null) => void;
  timelineItems: TimelineItem[];
};

type IntervieweeProfileMetaArgs = {
  selectedInterviewee: IntervieweeCard | null;
  intervieweeDetail: IntervieweeDetail | null;
};

export function useIntervieweeProfileMeta({
  selectedInterviewee,
  intervieweeDetail,
}: IntervieweeProfileMetaArgs): IntervieweeProfileMeta {
  const [activeViolation, setActiveViolation] = useState<string | null>(null);

  const summaryTags = useMemo(() => {
    const directTags = filterIntervieweeViolationTags(intervieweeDetail?.tags ?? []);
    if (directTags.length > 0) {
      return directTags;
    }

    const summaryText = intervieweeDetail?.summaryText ?? selectedInterviewee?.summaryText ?? null;
    return extractIntervieweeViolationTags(summaryText);
  }, [intervieweeDetail?.summaryText, intervieweeDetail?.tags, selectedInterviewee?.summaryText]);

  const violationItems = useMemo(() => getIntervieweeViolations(summaryTags), [summaryTags]);

  const activeViolationText = useMemo(() => {
    if (violationItems.length > 0) {
      return violationItems.find((item) => item.tag === activeViolation)?.sentence
        ?? "Human rights violations reported by the survivor.";
    }

    return selectedInterviewee
      ? "No reported violations are available yet."
      : "Select an interviewee card to load reported violations.";
  }, [activeViolation, selectedInterviewee, violationItems]);

  const timelineItems = useMemo(
    () => ([
      {
        primary: selectedInterviewee?.displacementYear ?? "Unknown",
        secondary: "Displacement year",
      },
      {
        primary: "Context",
        secondary: selectedInterviewee?.currentContext ?? "Pending",
      },
    ]),
    [selectedInterviewee?.currentContext, selectedInterviewee?.displacementYear],
  );

  return {
    summaryTags,
    violationItems,
    activeViolationText,
    activeViolation,
    setActiveViolation,
    timelineItems,
  };
}
